import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom'
import {Plug, GitMerge, Settings2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {InlineRow} from '@/components/shared/InlineRow'
import {ContentArea} from '@/components/shared/ContentArea'
import {SidebarNav, type SidebarNavItem} from '@/components/layout/SidebarNav'
import {SidebarNavId} from '@/testids/SidebarNavTestIds'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {useViewportBelow} from '@/hooks/use-viewport-breakpoint'

const MOBILE_BREAKPOINT_PX = 768

export function InboundConnectorConfigLayout() {
    const i18n = useI18n()
    const {id} = useParams<{id: string}>()
    const location = useLocation()
    const navigate = useNavigate()
    const isMobile = useViewportBelow(MOBILE_BREAKPOINT_PX)

    if (!id) {
        return null
    }

    const items: SidebarNavItem[] = [
        {
            id: SidebarNavId.InboundConnectorConfigConnection,
            title: i18n.inboundConnectors.configNavConnection,
            href: routes.connectors.inbound.configuration.connection(id),
            icon: Plug,
            description: i18n.inboundConnectors.configNavConnectionDescription,
        },
        {
            id: SidebarNavId.InboundConnectorConfigReconciliation,
            title: i18n.inboundConnectors.configNavReconciliation,
            href: routes.connectors.inbound.configuration.reconciliation(id),
            icon: GitMerge,
            description: i18n.inboundConnectors.configNavReconciliationDescription,
        },
        {
            id: SidebarNavId.InboundConnectorConfigOperations,
            title: i18n.inboundConnectors.configNavOperations,
            href: routes.connectors.inbound.configuration.operations(id),
            icon: Settings2,
            description: i18n.inboundConnectors.configNavOperationsDescription,
        },
    ]

    const activeItem = items.find(item => location.pathname.startsWith(item.href)) ?? items[0]

    if (isMobile) {
        return (
            <Stack gap="md" data-testid="inbound-config-layout-mobile">
                <Select
                    value={activeItem.href}
                    onValueChange={(href) => navigate(href)}
                >
                    <SelectTrigger
                        data-testid="select-config-subpage"
                        aria-label={i18n.inboundConnectors.configNavSelectorAria}
                    >
                        <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                        {items.map(item => (
                            <SelectItem
                                key={item.href}
                                value={item.href}
                                data-testid={`select-config-subpage-option-${item.href.split('/').pop()}`}
                            >
                                {item.title}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <ContentArea>
                    <Outlet/>
                </ContentArea>
            </Stack>
        )
    }

    return (
        <InlineRow align="start" gap="lg" data-testid="inbound-config-layout">
            <SidebarNav items={items} aria-label={String(i18n.inboundConnectors.configNavConnection)}/>
            <ContentArea>
                <Outlet/>
            </ContentArea>
        </InlineRow>
    )
}
